import { useEffect, useRef, useState } from "react";
import { Share2, Link2, Check } from "lucide-react";
import "./VendorShareButton.css";

// Small inline brand marks — avoids pulling in a new icon package just for these three.
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.868-2.03-.967-.273-.099-.472-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12.02 2C6.5 2 2 6.48 2 12c0 1.85.5 3.58 1.36 5.07L2 22l5.06-1.33A9.94 9.94 0 0 0 12.02 22C17.54 22 22 17.52 22 12S17.54 2 12.02 2zm0 18.1c-1.6 0-3.13-.44-4.47-1.24l-.32-.19-3.03.8.8-2.95-.2-.31a8.06 8.06 0 0 1-1.26-4.31c0-4.47 3.65-8.1 8.14-8.1 2.17 0 4.21.85 5.75 2.39a8.06 8.06 0 0 1 2.39 5.71c0 4.47-3.66 8.1-8.15 8.1z"/>
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.332.014 7.052.072 2.695.272.273 2.69.073 7.052.014 8.332 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.332 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.668-.072-4.948-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
  </svg>
);

export default function VendorShareButton({ vendorName, slug }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  const shareUrl = `${window.location.origin}/v/${slug}`;
  const shareText = vendorName ? `Check out ${vendorName} on BizBaz` : "Check out this vendor on BizBaz";

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleShareClick = async () => {
    // Native share sheet — on mobile this already lists Instagram, WhatsApp,
    // X/Twitter, Messages, etc. via whatever the person has installed.
    if (navigator.share) {
      try {
        await navigator.share({ title: shareText, text: shareText, url: shareUrl });
      } catch {
        // User cancelled the native sheet — no action needed.
      }
      return;
    }
    // No native share sheet (most desktop browsers) — open our own menu.
    setOpen((o) => !o);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard API blocked — fall back to a manual prompt.
      window.prompt("Copy this link:", shareUrl);
    }
  };

  const shareToWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const shareToX = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const shareToInstagram = async () => {
    // No web share target exists for Instagram — copy the link and let
    // the person paste it into their bio or a story themselves.
    await copyLink();
  };

  return (
    <div className="vsb-wrap">
      <button ref={btnRef} className="vsb-trigger" onClick={handleShareClick} aria-label="Share this profile">
        <Share2 size={15} />
        Share
      </button>

      {open && (
        <div ref={menuRef} className="vsb-menu" role="menu">
          <button className="vsb-item" onClick={copyLink} role="menuitem">
            {copied ? <Check size={16} /> : <Link2 size={16} />}
            {copied ? "Link copied" : "Copy link"}
          </button>
          <button className="vsb-item" onClick={shareToWhatsApp} role="menuitem">
            <WhatsAppIcon /> WhatsApp
          </button>
          <button className="vsb-item" onClick={shareToX} role="menuitem">
            <XIcon /> X
          </button>
          <button className="vsb-item" onClick={shareToInstagram} role="menuitem">
            <InstagramIcon /> Instagram
            <span className="vsb-item-hint">copies link</span>
          </button>
        </div>
      )}
    </div>
  );
}